
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface PromptTemplate {
  id: string;
  title: string;
  description: string;
  icon: string;
  category: string;
  category_id: string;
  estimated_time?: string;
  popularity?: {
    likes?: number;
    uses?: number;
  };
  is_favorite?: boolean;
  tags?: string[];
}

interface UseProductTemplatesProps {
  categoryFilter?: string;
  sortBy?: "popular" | "recent" | "alphabetical";
  limit?: number;
  searchQuery?: string;
}

export const useProductTemplates = ({ 
  categoryFilter, 
  sortBy = "popular",
  limit = 30,
  searchQuery = ""
}: UseProductTemplatesProps = {}) => {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  
  const fetchProductTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("Fetching product templates with filter:", categoryFilter, "sort:", sortBy, "limit:", limit);
      
      // Start query builder
      let query = supabase
        .from('prompts')
        .select(`
          id, 
          title, 
          description,
          likes,
          reviews,
          copies,
          category_id,
          categories(name)
        `)
        .eq('status', 'approved');
      
      // Apply search query if provided
      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
      }
      
      // Apply category filter if provided
      if (categoryFilter) {
        // Check if it's one of our special categories or a DB category ID
        if (categoryFilter === "prd") {
          // Look for PRD-related templates
          query = query.or(`title.ilike.%PRD%,title.ilike.%Product Requirement%,categories.name.ilike.%PRD%,categories.name.ilike.%Product Requirement%`);
        } else if (categoryFilter === "brainstorm") {
          // Look for brainstorming-related templates
          query = query.or(`title.ilike.%Brainstorm%,title.ilike.%Idea%,categories.name.ilike.%Brainstorm%,categories.name.ilike.%Idea%`);
        } else if (categoryFilter === "research") {
          // Look for research-related templates
          query = query.or(`title.ilike.%Research%,title.ilike.%Market%,categories.name.ilike.%Research%,categories.name.ilike.%Market%`);
        } else if (categoryFilter === "voc") {
          // Look for Voice of Customer related templates
          query = query.or(`title.ilike.%Voice of Customer%,title.ilike.%VOC%,title.ilike.%Feedback%,title.ilike.%User Need%,categories.name.ilike.%Customer%,categories.name.ilike.%User%`);
        } else {
          // If it's a UUID, use it directly as category_id
          query = query.eq('category_id', categoryFilter);
        }
      }
      
      // Apply sorting
      switch (sortBy) {
        case "popular":
          query = query.order('copies', { ascending: false }).order('likes', { ascending: false });
          break;
        case "recent":
          query = query.order('created_at', { ascending: false });
          break;
        case "alphabetical":
          query = query.order('title', { ascending: true });
          break;
      }
      
      // Apply limit
      query = query.limit(limit);
      
      console.log("Executing Supabase query for templates...");
      const { data, error } = await query;
      
      if (error) {
        console.error("Supabase query error:", error);
        throw error;
      }
      
      console.log("Templates query result:", data ? data.length : 0, "templates found");
      
      if (!data || data.length === 0) {
        console.log("No templates found with the current filters");
        setTemplates([]);
        setIsLoading(false);
        return;
      }
      
      // Format data with icons and category info
      const formattedTemplates = data.map(prompt => {
        // Generate some mock tags for demonstration purposes
        const mockTags = generateMockTags(prompt.title, prompt.categories?.name);
        
        return {
          id: prompt.id,
          title: prompt.title,
          description: prompt.description,
          category: prompt.categories?.name || 'Uncategorized',
          category_id: prompt.category_id,
          // Determine estimated time based on content length and complexity
          estimated_time: estimateTimeForPrompt(prompt.title, prompt.description),
          // Add popularity metrics
          popularity: {
            likes: prompt.likes || 0,
            uses: prompt.copies || 0
          },
          icon: determineIconType(prompt.title, prompt.categories?.name),
          tags: mockTags
        };
      });
      
      console.log("Formatted templates:", formattedTemplates.length);
      setTemplates(formattedTemplates);
    } catch (error: any) {
      console.error("Error fetching templates:", error);
      setError(error.message || "Failed to load templates");
      toast({
        title: "Error",
        description: "Failed to load templates. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [categoryFilter, sortBy, limit, searchQuery, toast]);

  useEffect(() => {
    console.log("useProductTemplates hook triggered, calling fetchProductTemplates");
    fetchProductTemplates();
  }, [fetchProductTemplates]);

  // Helper function to generate mock tags for demonstration
  const generateMockTags = (title: string, category?: string): string[] => {
    const allTags = ["PRD", "Feature", "User Research", "Market", "Business", "Technical", "High Priority", "Customer"];
    const tags = [];
    
    const lowerTitle = title.toLowerCase();
    
    // Add tags based on title content
    if (lowerTitle.includes("prd") || lowerTitle.includes("requirement")) tags.push("PRD");
    if (lowerTitle.includes("feature") || lowerTitle.includes("functionality")) tags.push("Feature");
    if (lowerTitle.includes("user") || lowerTitle.includes("customer")) tags.push("User Research");
    if (lowerTitle.includes("market") || lowerTitle.includes("competitor")) tags.push("Market");
    
    // Ensure we have at least one tag
    if (tags.length === 0) {
      tags.push(allTags[Math.floor(Math.random() * allTags.length)]);
    }
    
    // Limit to max 2 tags
    return tags.slice(0, 2);
  };

  // Helper function to estimate time based on title and description
  const estimateTimeForPrompt = (title: string, description: string): string => {
    const combinedText = (title + description).toLowerCase();
    
    // Check for complexity indicators in the text
    if (combinedText.includes('comprehensive') || 
        combinedText.includes('detailed') || 
        combinedText.includes('full') || 
        description.length > 500) {
      return '15-20 min';
    } else if (combinedText.includes('brief') || 
               combinedText.includes('quick') || 
               description.length < 200) {
      return '5-10 min';
    }
    
    // Default time estimate
    return '10-15 min';
  };
  
  return { 
    templates, 
    isLoading, 
    error, 
    refetch: fetchProductTemplates 
  };
};

// Helper function to determine icon type based on template title or category
export const determineIconType = (title: string, category?: string): string => {
  const lowerTitle = title.toLowerCase();
  const lowerCategory = category ? category.toLowerCase() : '';
  
  // Check for PRD category
  if (
    lowerTitle.includes("prd") || 
    lowerTitle.includes("product requirement") || 
    lowerTitle.includes("product spec") || 
    lowerCategory.includes("prd") ||
    lowerCategory.includes("product requirement")
  ) {
    return "prd";
  } 
  // Check for brainstorming/ideation category
  else if (
    lowerTitle.includes("brainstorm") || 
    lowerTitle.includes("idea") || 
    lowerTitle.includes("concept") ||
    lowerTitle.includes("creative") ||
    lowerCategory.includes("brainstorm") ||
    lowerCategory.includes("ideation")
  ) {
    return "brainstorm";
  } 
  // Check for market research category
  else if (
    lowerTitle.includes("research") || 
    lowerTitle.includes("market") || 
    lowerTitle.includes("competitor") ||
    lowerTitle.includes("analysis") ||
    lowerCategory.includes("research") ||
    lowerCategory.includes("market")
  ) {
    return "research";
  } 
  // Check for customer insights/VOC category
  else if (
    lowerTitle.includes("voice of customer") || 
    lowerTitle.includes("voc") || 
    lowerTitle.includes("feedback") || 
    lowerTitle.includes("customer") ||
    lowerTitle.includes("user need") ||
    lowerTitle.includes("persona") ||
    lowerCategory.includes("voc") ||
    lowerCategory.includes("customer") ||
    lowerCategory.includes("user")
  ) {
    return "voc";
  }
  
  return "default";
};
